import { Col, Form, Image, Input, Modal, Radio, Row, Select, Checkbox } from "antd";
import React, { useEffect, useState } from "react";

import { Severty, ShowToast } from "../../helper/toast";
import useRequest from "../../hooks/useRequest";
import lang from "../../helper/langHelper";
import notfound from "../../assets/images/not_found.png";
import SingleImageUpload from "../../components/SingleImageUpload";

const AddForm = ({ api, show, hide, data, refresh }) => {
  const [form] = Form.useForm();
  const { request } = useRequest();
  const [loading, setLoading] = useState(false);
  const [category, setCategory] = useState([]);
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(notfound);
  const [isImageRemove, setIsImageRemove] = useState(false);
  const FileType = ["image/png", "image/jpg", "image/jpeg", "image/avif", "image/webp", "image/gif"];

  const normalizeCategoryOptions = (items = []) =>
    (Array.isArray(items) ? items : [])
      .map((item) => ({
        value: item?._id || item?.value || item?.id || item,
        name: item?.name || item?.label || item?.title || item?.value || item?._id || item,
      }))
      .filter((item) => item.value);

  const normalizeCategoryIds = (value) => {
    if (typeof value === "string") {
      if (value.trim().startsWith("[")) {
        try {
          const parsedValue = JSON.parse(value);
          if (Array.isArray(parsedValue)) {
            return parsedValue.map((item) => item?._id || item?.value || item?.id || item).filter(Boolean);
          }
        } catch {
          // Fall back to comma-separated parsing below.
        }
      }

      const parsedValue = value
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean);
      return parsedValue;
    }

    if (!Array.isArray(value)) {
      return [];
    }

    return value
      .map((item) => item?._id || item?.id || item?.value || item)
      .filter(Boolean);
  };

  const handleImage = (file) => {
    if (!file) {
      setIsImageRemove(Boolean(data));
      setImage(null);
      form.validateFields(["image"]);
      return;
    }
    setIsImageRemove(false);
    setImage(file || null);
    form.validateFields(["image"]);
  };
  const getCategory = () => {
    request({
      url: api.categories,
      method: "GET",
      onSuccess: ({ data, status }) => {
        if (status) {
          setCategory(normalizeCategoryOptions(data?.docs || data));
        }
      },
      onError: (err) => {
        console.log(err);
      },
    });
  };

  useEffect(() => {
    getCategory();
  }, []);

  useEffect(() => {
    if (!data) {
      form.resetFields();
      setImage(null);
      setImagePreview(notfound);
      setIsImageRemove(false);
      return;
    }

    const categoryIds = normalizeCategoryIds(data?.category_ids || data?.category || []);
    form.setFieldsValue({
      ...data,
      category_ids: categoryIds,
    });
    setImage(data?.image || null);
    setIsImageRemove(false);
  }, [data, form]);

  useEffect(() => {
    if (!image) {
      setImagePreview(notfound);
      return;
    }

    const selectedFile = image instanceof File ? image : image?.originFileObj;
    if (selectedFile instanceof File) {
      const filePreview = URL.createObjectURL(selectedFile);
      setImagePreview(filePreview);
      return () => URL.revokeObjectURL(filePreview);
    }

    if (typeof image === "string") {
      setImagePreview(image);
      return;
    }

    if (image?.url) {
      setImagePreview(image.url);
      return;
    }

    if (image?.thumbUrl) {
      setImagePreview(image.thumbUrl);
      return;
    }

    setImagePreview(notfound);
  }, [image]);

  const onCreate = (values) => {
    setLoading(true);
    const payload = new FormData();
    payload.append("name", values.name || "");
    payload.append("hi_name", values.hi_name || "");
    payload.append("sort_number", values.sort_number || "");
    payload.append("is_active", values.is_active ?? true);
    payload.append("is_featured", values.is_featured ? true : false);
    payload.append("category_ids", Array.isArray(values.category_ids) ? values.category_ids.filter(Boolean).join(",") : "");

    const selectedFile = image instanceof File ? image : image?.originFileObj;
    if (selectedFile instanceof File) {
      payload.append("image", selectedFile);
    }
    if (data) {
      payload.append("isImageRemove", isImageRemove ? true : false);
    }

    request({
      url: `${data ? api.addEdit + "/" + data._id : api.addEdit}`,
      method: data ? "PUT" : "POST",
      data: payload,
      header: {
        "Content-Type": "multipart/form-data",
      },
      onSuccess: (data) => {
        setLoading(false);
        if (data.status) {
          ShowToast(data.message, Severty.SUCCESS);
          hide();
          refresh();
        } else {
          ShowToast(data.message, Severty.ERROR);
        }
      },
      onError: (error) => {
        ShowToast(error?.response?.data?.message || error?.message || "Something went wrong", Severty.ERROR);
        setLoading(false);
      },
    });
  };

  return (
    <Modal
      open={show}
      width={750}
      okText={data ? lang(`Update`) : lang(`Add`)}
      cancelText={lang(`Cancel`)}
      onCancel={hide}
      okButtonProps={{
        form: "create",
        htmlType: "submit",
        loading: loading,
      }}
      aria-labelledby="contained-modal-title-vcenter"
      centered
      className="tab_modal"
    >
      <Form
        id="create"
        form={form}
        onFinish={onCreate}
        layout="vertical"
        initialValues={{
          is_active: true,
        }}
      >
        <h4 className="modal_title_cls">{data ? lang(`Edit Sub Category`) : lang(`Add New Sub Category`)}</h4>
        <Row gutter={[16, 0]}>
          <Col span={24} sm={24}>
            <Form.Item
              label={lang("Categories")}
              name="category_ids"
              rules={[
                {
                  required: true,
                  message: lang("Please select the categories!"),
                },
              ]}
            >
              <Select
                getPopupContainer={(triggerNode) => triggerNode.parentNode}
                mode="multiple"
                filterOption={(input, option) => (option?.label ? option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0 : false)}
                placeholder={lang("Select Categories")}
                showSearch
                optionFilterProp="label"
              >
                {category.map((item) => (
                  <Select.Option
                    key={item.id || item._id || item.value}
                    label={item.label || item.name}
                    value={item.id || item._id || item.value}
                  >
                    {item.label || item.name}{" "}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={24} sm={12}>
            <Form.Item
              label={lang(`Sub Category Name`)}
              name="name"
              rules={[
                {
                  required: true,
                  message: lang("Name is required"),
                },
                {
                  max: 200,
                  message: lang("Name should not contain more then 200 characters!"),
                },
                {
                  min: 2,
                  message: lang("Name should contain at least 2 characters!"),
                },
              ]}
              normalize={(value) => value.trimStart()}
            >
              <Input autoComplete="off" placeholder={lang(`Enter Sub Category Name`)} />
            </Form.Item>
          </Col>

          <Col span={24} sm={12}>
            <Form.Item
              label={lang(`Sub Category Name Hindi`)}
              name="hi_name"
              rules={[
                {
                  required: true,
                  message: lang("Hindi Name is required"),
                },
                {
                  max: 200,
                  message: lang("Name should not contain more then 200 characters!"),
                },
                {
                  min: 2,
                  message: lang("Name should contain at least 2 characters!"),
                },
              ]}
              normalize={(value) => value.trimStart()}
            >
              <Input autoComplete="off" placeholder={`Enter Category Name Hindi`} />
            </Form.Item>
          </Col>

          <Col span={24} sm={12}>
            <Form.Item
              label={lang(`Sort Number (Unique)`)}
              name="sort_number"
              rules={[
                {
                  required: true,
                  message: lang("Sort Number is required"),
                },
              ]}
              normalize={(value) => value.trimStart()}
            >
              <Input autoComplete="off" type="number" placeholder={`Enter Sort Number`} />
            </Form.Item>
          </Col>

          <Col span={12} sm={12}>
            <Form.Item label={lang("")} name="is_featured" valuePropName="checked">
              <Checkbox>Featured</Checkbox>
            </Form.Item>
          </Col>
          <Col span={12} sm={12}>
            <div className="status_wrap">
              <Form.Item label={lang("Status")} name="is_active">
                <Radio.Group>
                  <Radio value={true}>{lang("Active")}</Radio>
                  <Radio value={false}>{lang("De-Active")}</Radio>
                </Radio.Group>
              </Form.Item>
            </div>
          </Col>

          <Col span={24}>
            <Form.Item
              className=""
              label={lang("Upload Image")}
              name="image"
            >
              <SingleImageUpload value={image} fileType={FileType} btnName={"Image"} imageType="advertisement" onChange={(data) => handleImage(data)} isDimension={true} />
              <p className="img-size-details">
                **
                {lang("Images should be 600x400 for best view in gallery image. You can select only (.gif, .png, .jpeg, .jpg) format files upto 1 MB file size")}
                ..!!!
              </p>
              {
                <div className="mt-2">
                  <Image width={120} src={imagePreview}></Image>
                </div>
              }
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default AddForm;
